import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Button,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';

const ServicesList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [priceFilter, setPriceFilter] = useState('all');
  
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/services');
        console.log('Полученные данные:', response.data);
        
        // Проверяем структуру данных и извлекаем массив услуг
        let servicesData = [];
        if (response.data && response.data.services) {
          servicesData = response.data.services;
        } else {
          // Если данные не в ожидаемом формате, используем как есть
          servicesData = Array.isArray(response.data) ? response.data : [];
        }
        
        // Проверяем цены для отладки
        servicesData.forEach(service => {
          console.log(`Услуга ${service.name}:`, {
            priceType: service.priceType,
            basePrice: service.basePrice,
            hourlyPrice: service.hourlyPrice,
            dailyPrice: service.dailyPrice,
            basePrice_type: typeof service.basePrice,
            hourlyPrice_type: typeof service.hourlyPrice,
            dailyPrice_type: typeof service.dailyPrice
          });
        });
        
        setServices(servicesData);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Не удалось загрузить список услуг. Пожалуйста, попробуйте позже.');
        setLoading(false);
      }
    };
    
    fetchServices();
  }, []);
  
  // Filter services based on search query and price type
  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (service.description && service.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesPriceType = priceFilter === 'all' || service.priceType === priceFilter;
    
    return matchesSearch && matchesPriceType && service.isActive;
  });
  
  // Format price based on price type
  const formatPrice = (service) => {
    switch (service.priceType) {
      case 'fixed':
        return service.basePrice ? `ОТ ${Math.floor(service.basePrice)} ₽` : 'Цена по запросу';
      case 'hourly':
        return service.hourlyPrice ? `ОТ ${Math.floor(service.hourlyPrice)} ₽/час` : 'Цена по запросу';
      case 'daily':
        return service.dailyPrice ? `ОТ ${Math.floor(service.dailyPrice)} ₽/день` : 'Цена по запросу';
      default:
        return service.basePrice ? `ОТ ${Math.floor(service.basePrice)} ₽` : 'Цена по запросу';
    }
  };
  
  // Get price type label
  const getPriceTypeLabel = (priceType) => {
    switch (priceType) {
      case 'fixed':
        return 'Фиксированная цена';
      case 'hourly':
        return 'Почасовая оплата';
      case 'daily':
        return 'Посуточная оплата';
      default:
        return 'Фиксированная цена';
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Главная
        </Link>
        <Typography color="text.primary">Услуги</Typography>
      </Breadcrumbs>
      
      {/* Page title */}
      <Typography variant="h4" component="h1" gutterBottom>
        Наши услуги
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Мы предлагаем широкий спектр услуг для вашего комфортного отдыха
      </Typography>
      <Divider sx={{ mb: 4 }} />
      
      {/* Filters */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <TextField
          placeholder="Поиск услуг..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Тип оплаты</InputLabel>
          <Select
            value={priceFilter}
            label="Тип оплаты"
            onChange={(e) => setPriceFilter(e.target.value)}
          >
            <MenuItem value="all">Все типы</MenuItem>
            <MenuItem value="fixed">Фиксированная</MenuItem>
            <MenuItem value="hourly">Почасовая</MenuItem>
            <MenuItem value="daily">Посуточная</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}
      
      {/* Loading indicator */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Services list */}
          {filteredServices.length > 0 ? (
            <Grid container spacing={4}>
              {filteredServices.map((service) => (
                <Grid item key={service.id} xs={12} sm={6} md={4}>
                  <Card 
                    elevation={3}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 6
                      }
                    }}
                  >
                    <CardActionArea 
                      component={RouterLink}
                      to={`/services/${service.slug}`}
                      sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={service.featuredImage || '/images/placeholder-service.jpg'}
                        alt={service.name}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography gutterBottom variant="h6" component="div" sx={{ mb: 0 }}>
                            {service.name}
                          </Typography>
                          <Chip 
                            label={formatPrice(service)} 
                            color="primary" 
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {getPriceTypeLabel(service.priceType)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.description && service.description.length > 100
                            ? `${service.description.substring(0, 100)}...`
                            : service.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button 
                        fullWidth 
                        variant="contained" 
                        component={RouterLink}
                        to={`/services/${service.slug}`}
                      >
                        Подробнее
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6">
                {searchQuery || priceFilter !== 'all'
                  ? 'Нет услуг, соответствующих выбранным фильтрам'
                  : 'Нет доступных услуг'}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default ServicesList;
